/**
 * Entry page for the admin-issued authenticator enrollment invitation flow.
 *
 * Flow:
 * 1. Reads the magic token from the URL params.
 * 2. Calls the BFF /enrollment-invitation/start to validate the token and
 *    establish the enrollment_invitation session cookie. This also returns a
 *    seed OnboardingFlowResponse that drives the first step of the wizard.
 * 3. Renders ``InvitationOnboardingWizard``, which reuses the shared
 *    onboarding view components (factor steps, alternatives, success screen)
 *    via the dependency-injected OnboardingApiClient.
 * 4. On the user's explicit acknowledgement, renders the terminal success
 *    screen. No session / token exchange happens here.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import {
  startInvitation,
  type InvitationStartResponse,
} from '@/features/enrollment-invitation/api/enrollment-invitation-api';
import { EnrollmentInvitationSuccessScreen } from '@/features/enrollment-invitation/components/enrollment-invitation-success-screen';
import { InvitationOnboardingWizard } from '@/features/enrollment-invitation/components/invitation-onboarding-wizard';
// TODO(cross-feature-reuse): lift the onboarding wizard chrome (background,
// container, Dialog primitive) into @/components/factor-enrollment/ so this
// feature stops reaching across the boundary.
/* eslint-disable boundaries/element-types */
import { OnboardingBackground } from '@/features/onboarding/components/onboarding-background';
import { OnboardingWizardContainer } from '@/features/onboarding/components/onboarding-wizard-container';
import { Onboarding } from '@/features/onboarding/components/primitives';
/* eslint-enable boundaries/element-types */

type PageState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | {
      kind: 'ready';
      initialFlow: InvitationStartResponse;
      requestedFactors: string[];
    }
  | { kind: 'complete'; enrolledFactors: string[] }
  | { kind: 'error' };

export default function EnrollmentInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>({ kind: 'loading' });

  // React 18 StrictMode double-invokes effects in dev. The old guard (a
  // ``cancelled`` flag in the cleanup) only suppresses the SECOND response
  // from updating state — it doesn't prevent the SECOND network request from
  // going out. For WebAuthn that's fatal: each /start call makes the
  // enrollment service generate a fresh registration challenge, and the
  // second challenge overwrites the first in Redis. The browser then signs
  // the stale first challenge and `/registration/complete` rejects it with
  // "Wrong challenge in response."
  //
  // A module-scoped ref keyed by magic token fires the /start call exactly
  // once per page-mount.
  const hasStartedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      // Invalid URL without a token.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState({ kind: 'invalid' });

      return;
    }

    if (hasStartedRef.current === token) {
      return;
    }

    hasStartedRef.current = token;

    // Note: no ``cancelled``-flag cleanup. StrictMode would set it to true on
    // the first unmount, and because the ref guard skips the second effect,
    // nothing would ever reset it — the resolved promise from the real
    // /start call would be dropped and the page would be stuck "loading"
    // forever. The ref guard is sufficient: the /start call fires exactly
    // once per page-mount per token, and its response always belongs to the
    // current component.
    startInvitation(token)
      .then(response => {
        if (!response.valid) {
          // Deliberately NOT showing ``response.reason`` to the user — that
          // would let an attacker probing random tokens distinguish "invalid"
          // from "expired" from "completed" from "invalidated". The BFF logs
          // the specific reason for support debugging.
          setState({ kind: 'invalid' });

          return;
        }

        // Short-circuit: the BE auto-satisfied every requested factor at
        // redemption time (e.g. the user had already enrolled a password
        // before clicking the link). No wizard needed — jump to success.
        if (response.status === 'completed') {
          setState({
            kind: 'complete',
            enrolledFactors: response.requestedFactors ?? [],
          });

          return;
        }

        setState({
          kind: 'ready',
          initialFlow: response,
          requestedFactors: response.requestedFactors ?? [],
        });
      })
      .catch(() => {
        // Same rationale as above: do not surface raw errors to the user.
        setState({ kind: 'error' });
      });
  }, [token]);

  if (state.kind === 'loading') {
    return (
      <OnboardingWizardContainer>
        <OnboardingBackground />
        <Onboarding.Dialog open={true} dismissible={false} size='md'>
          <Onboarding.Loading />
        </Onboarding.Dialog>
      </OnboardingWizardContainer>
    );
  }

  if (state.kind === 'invalid') {
    return (
      <OnboardingWizardContainer>
        <OnboardingBackground />
        <Onboarding.Dialog open={true} dismissible={false} size='md'>
          <div
            data-testid='enrollment-invitation-invalid'
            className='flex flex-col items-center text-center space-y-6 py-8'
          >
            <Onboarding.Header>
              <Onboarding.Title>Link is no longer valid</Onboarding.Title>
              <Onboarding.Description>
                Please ask your administrator to issue a new invitation.
              </Onboarding.Description>
            </Onboarding.Header>
          </div>
        </Onboarding.Dialog>
      </OnboardingWizardContainer>
    );
  }

  if (state.kind === 'error') {
    return (
      <OnboardingWizardContainer>
        <OnboardingBackground />
        <Onboarding.Dialog open={true} dismissible={false} size='md'>
          <div
            data-testid='enrollment-invitation-error'
            className='flex flex-col items-center text-center space-y-6 py-8'
          >
            <Onboarding.Header>
              <Onboarding.Title>Something went wrong</Onboarding.Title>
              <Onboarding.Description>
                Please try reopening the invitation link, or ask your
                administrator to issue a new one.
              </Onboarding.Description>
            </Onboarding.Header>
          </div>
        </Onboarding.Dialog>
      </OnboardingWizardContainer>
    );
  }

  if (state.kind === 'complete') {
    return (
      <OnboardingWizardContainer>
        <OnboardingBackground />
        <Onboarding.Dialog open={true} dismissible={false} size='md'>
          <EnrollmentInvitationSuccessScreen
            enrolledFactors={state.enrolledFactors}
          />
        </Onboarding.Dialog>
      </OnboardingWizardContainer>
    );
  }

  return (
    <OnboardingWizardContainer>
      <OnboardingBackground />
      <Onboarding.Dialog open={true} dismissible={false} size='md'>
        <InvitationOnboardingWizard
          initialFlowResponse={state.initialFlow}
          onComplete={enrolledFactors =>
            setState({
              kind: 'complete',
              // Prefer the list reported by the wizard (actually enrolled);
              // fall back to what the admin requested if for some reason the
              // wizard didn't populate it.
              enrolledFactors:
                enrolledFactors.length > 0
                  ? enrolledFactors
                  : state.requestedFactors,
            })
          }
        />
      </Onboarding.Dialog>
    </OnboardingWizardContainer>
  );
}
